import { ReactElement } from 'react'

import { useAtomValue } from 'jotai'

import {
  SequenceMenu,
  useNotifications,
  SimpleModal
} from '@sequencehq/core-components'
import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import { RedRed60 } from '@sequencehq/core-components'

import {
  isSavePendingAtom,
  invoiceIdAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { useCloseInvoiceEditor } from 'Invoices/hooks/useCloseInvoiceEditor'
import { usePermissions } from 'lib/rbac'

export const MenuItemDeleteInvoice = () => {
  const isSavePending = useAtomValue(isSavePendingAtom)

  const { can } = usePermissions()

  if (!can('org.invoice:delete')) {
    return null
  }

  return (
    <DialogDeleteInvoice
      trigger={
        <SequenceMenu.Item
          uuid="delete-invoice"
          label="Delete invoice"
          style={{ color: RedRed60 }}
          isDisabled={isSavePending}
          data-testid="header.kebab-menu.delete-invoice"
        />
      }
    />
  )
}

const DialogDeleteInvoice = ({ trigger }: { trigger: ReactElement }) => {
  const { displayNotification } = useNotifications()
  const closeInvoiceEditor = useCloseInvoiceEditor()

  const invoiceId = useAtomValue(invoiceIdAtom)

  const deleteInvoiceMutation = useMutation(
    dashboard20240730Client.deleteInvoice,
    {
      onSuccess: () => {
        closeInvoiceEditor()
        displayNotification('Invoice deleted', { type: 'success' })
      },
      onError: () => {
        displayNotification('Failed to delete invoice', {
          type: 'error'
        })
      }
    }
  )

  return (
    <SimpleModal
      data-testid="modal.delete-invoice"
      trigger={trigger}
      variant="dialog"
      title="Delete invoice"
      submitButtonText="Delete invoice"
      submittingButtonText="Deleting..."
      isDestructive
      onSubmit={async () => {
        if (!invoiceId) return false
        const res = await deleteInvoiceMutation.mutateAsync({
          invoiceId
        })
        return Boolean(res)
      }}
    >
      This will delete the invoice immediately. This action cannot be undone.
    </SimpleModal>
  )
}
